import React, { useEffect, useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Save, User, Hourglass } from "lucide-react";
import { SYRIAN_CITIES } from "../lib/constants";
import MaterialSelector from "../components/MaterialSelector";
import StarRating from "../components/StarRating";
import { useAuth } from "../context/AuthContext";
import { logger } from "../lib/logger";

const NAME_FIELDS = ["first_name", "last_name", "store_name"];

const Profile = () => {
  const { setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [orig, setOrig] = useState(null);
  const [rating, setRating] = useState({ avg: 0, count: 0 });
  const [pendingReq, setPendingReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPending = async () => {
    try {
      const r = await api.get("/merchant/name-change/pending");
      setPendingReq(r.data?.pending || null);
    } catch (err) { logger.warn("pending name-change fetch failed:", err); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/merchant/profile");
        const data = {
          first_name: r.data.first_name || "",
          last_name: r.data.last_name || "",
          store_name: r.data.store_name || "",
          city: r.data.city || SYRIAN_CITIES[0],
          detailed_address: r.data.detailed_address || "",
          phone_number: r.data.phone_number || "",
          material_types: r.data.material_types || [],
          email: r.data.email,
        };
        setForm(data);
        setOrig(data);
        setRating({ avg: r.data.rating_avg || 0, count: r.data.rating_count || 0 });
        loadPending();
      } catch (e) { toast.error(arabicError(e)); } finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.material_types.length === 0) { toast.error("اختر نوع مواد واحد على الأقل"); return; }
    if (!form.phone_number.trim()) { toast.error("رقم الهاتف مطلوب"); return; }
    setSaving(true);
    try {
      // Name fields go through admin review; the rest apply directly
      const nameChanges = {};
      NAME_FIELDS.forEach((f) => {
        if ((form[f] || "").trim() && form[f].trim() !== (orig?.[f] || "")) nameChanges[f] = form[f].trim();
      });
      const r = await api.patch("/merchant/profile", {
        city: form.city,
        detailed_address: form.detailed_address,
        phone_number: form.phone_number,
        material_types: form.material_types,
      });
      setUser(r.data);
      localStorage.setItem("imdad_user", JSON.stringify(r.data));
      if (Object.keys(nameChanges).length > 0) {
        await api.post("/merchant/name-change", nameChanges);
        toast.info("تم إرسال طلب تعديل الاسم إلى الإدارة للمراجعة والموافقة", { duration: 6000 });
        setForm((s) => ({ ...s, first_name: orig.first_name, last_name: orig.last_name, store_name: orig.store_name }));
        loadPending();
      }
      toast.success("تم حفظ الملف الشخصي");
    } catch (err) { toast.error(arabicError(err)); } finally { setSaving(false); }
  };

  if (loading || !form) {
    return <div className="bg-white border border-[#7AB2B2] rounded-xl p-8 text-center text-[#666666]">جارٍ التحميل...</div>;
  }

  const inputCls = "h-12 w-full rounded-lg border border-[#7AB2B2] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E]";

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">الملف الشخصي</h1>
        <p className="text-[#666666] mt-1">بيانات متجرك وبيانات التواصل</p>
      </div>

      {/* Merchant rating */}
      <div className="bg-white border border-[#7AB2B2] rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="profile-rating-card">
        <div className="font-extrabold text-[#333333]">تقييمي كتاجر</div>
        {rating.count > 0 ? (
          <StarRating avg={rating.avg} count={rating.count} size={18} testid="profile-rating" />
        ) : (
          <span className="text-sm text-[#666666]" data-testid="profile-rating-empty">لا توجد تقييمات بعد — تظهر بعد إتمام عمليات البيع</span>
        )}
      </div>

      {pendingReq && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3" data-testid="pending-name-request">
          <Hourglass className="text-amber-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm">
            <div className="font-extrabold text-amber-800">لديك طلب تعديل اسم قيد مراجعة الإدارة</div>
            <div className="text-amber-700 mt-1">
              {Object.entries(pendingReq.requested || {}).map(([f, v]) => (
                <span key={f} className="ml-3">
                  {f === "first_name" ? "الاسم" : f === "last_name" ? "الكنية" : "اسم المحل"}: <b>{v}</b>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="bg-white border border-[#7AB2B2] rounded-2xl p-6 space-y-5" data-testid="profile-form">
        <div className="flex items-center gap-3 p-3 bg-[#EBF4F6] rounded-lg">
          <User className="text-[#09637E]" />
          <div>
            <div className="text-xs text-[#666666]">البريد الإلكتروني</div>
            <div className="font-bold text-[#333333]">{form.email}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">الاسم <span className="text-xs text-[#666666] font-normal">(يتطلب موافقة الإدارة)</span></label>
            <input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
              data-testid="profile-firstname-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">الكنية <span className="text-xs text-[#666666] font-normal">(يتطلب موافقة الإدارة)</span></label>
            <input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
              data-testid="profile-lastname-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">اسم المحل <span className="text-xs text-[#666666] font-normal">(يتطلب موافقة الإدارة)</span></label>
            <input required value={form.store_name} onChange={(e) => set("store_name", e.target.value)}
              data-testid="profile-store-input" className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-bold text-[#333333] mb-1">المحافظة</label>
            <select value={form.city} onChange={(e) => set("city", e.target.value)}
              data-testid="profile-city-input" className={inputCls}>
              {SYRIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-[#333333] mb-1">رقم الهاتف</label>
            <input required value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)}
              data-testid="profile-phone-input" className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-bold text-[#333333] mb-1">العنوان التفصيلي</label>
            <textarea value={form.detailed_address} onChange={(e) => set("detailed_address", e.target.value)}
              data-testid="profile-address-input" rows={2}
              className="w-full rounded-lg border border-[#7AB2B2] bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-[#333333] mb-2">أنواع المواد</label>
          <MaterialSelector value={form.material_types} onChange={(v) => set("material_types", v)} testidPrefix="profile-material" />
        </div>

        <button type="submit" disabled={saving} data-testid="profile-save"
          className="h-12 w-full md:w-auto px-8 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60">
          <Save size={18} />
          {saving ? "جارٍ الحفظ..." : "حفظ التغييرات"}
        </button>
      </form>
    </div>
  );
};

export default Profile;
